import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'

import { Grid } from '@material-ui/core'

import Formik from './Formik'
import { PrimaryButton, Loader, LabelPolicy, Alert, CleanButton } from '../default'
import { FormText, FormDropDown, FormPhone, FormCpf, FormCnpj } from './formFields'
import ReCAPTCHA from 'react-google-recaptcha'
import FormCheckbox from './formFields/FormCheckbox'
import FormCurrencyField from './formFields/FormCurrencyField'
import { useTranslation } from 'react-i18next'

function LoginForm({
  classes, fields, btnText, btnProps, errorMsg, successMsg, onSubmit, onClose, onUpdateValues,
  schema, initialValues, showLabelPolicy, showReCAPTCHA, showBtn, showCloseBtn, splitFields,
  spacing = 2,
  ...props
}) {
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [successReCAPTCHA, setSuccessReCAPTCHA] = useState(false);
  const { t } = useTranslation()

  const handleLabelPolicyChange = event => {
    setAcceptedPolicy(event.target.checked);
  }

  const handleReCAPTCHAChange = () => {
    setSuccessReCAPTCHA(true);
  }

  const handleReCAPTCHAExpiration = () => {
    setSuccessReCAPTCHA(false);
  }

  const shouldFieldSplit = id => {
    if (!splitFields) {
      return false;
    }

    let shouldSplit = false;

    splitFields.forEach(sf => {
      if (sf.includes(id)) {
        shouldSplit = true;
      }
    });

    return shouldSplit;
  }

  const renderField = ({ isLoading, ...fieldProps }, fkProps, index) => {
    if (isLoading) { return <Loader key='loader' /> }

    const getComponent = component => {
      return (
        <Grid key={index} item xs={12} md={shouldFieldSplit(fieldProps.id) ? 6 : 12}>
          {component}
        </Grid>
      )
    }

    switch (fieldProps.type) {
      case 'password':
      case 'number':
      case 'text': return getComponent(renderTextField(fieldProps, fkProps, FormText))
      case 'phone': return getComponent(renderTextField(fieldProps, fkProps, FormPhone))
      case 'cpf': return getComponent(renderTextField(fieldProps, fkProps, FormCpf))
      case 'cnpj': return getComponent(renderTextField(fieldProps, fkProps, FormCnpj))
      case 'combobox': return getComponent(renderComboBox(fieldProps, fkProps))
      case 'checkbox': return getComponent(renderCheckbox(fieldProps, fkProps))
      case 'currency': return getComponent(renderTextField(fieldProps, fkProps, FormCurrencyField))
      default: {
        return null
      }
    }
  }

  const renderTextField = ({ id, label, ...rest }, fkProps, Comp) => {
    return (
      <Comp
        key={id}
        id={id}
        text={label}
        className={classes.field}
        fkProps={fkProps}
        style={{ width: '100%' }}
        {...rest}
      />
    )
  }

  const renderComboBox = (fieldProps, fkProps) => {
    return (
      <FormDropDown
        key={fieldProps.id}
        className={classes.field}
        fkProps={fkProps}
        style={{ width: '100%' }}
        {...fieldProps}
      />
    )
  }

  const renderCheckbox = (fieldProps, fkProps) => {
    return (
      <FormCheckbox
        key={fieldProps.id}
        className={classes.field}
        fkProps={fkProps}
        style={{ width: '100%' }}
        renderField={renderField}
        {...fieldProps}
      />
    )
  }

  const renderErrorMsg = () => {
    if (!errorMsg) { return null }

    return !errorMsg ? null : (
      <Alert text={errorMsg} className={classes.alert} variant="filled" color="error" />
    )
  }

  const renderSuccessMsg = () => {
    if (!successMsg) { return null }

    return !successMsg ? null : (
      <Alert text={successMsg} className={classes.alert} variant="filled" color="success" />
    )
  }

  return (
    <Formik
      onSubmit={(values, { setSubmitting }) => {
        if (showLabelPolicy && !acceptedPolicy)
          return
        if (showReCAPTCHA && !successReCAPTCHA)
          return
        onSubmit(values).then(_ => {
          setSubmitting(false)
        })
      }}
      initialValues={initialValues}
      validationSchema={schema}
      enableReinitialize={false}
      formProps={{ className: classes.form }}
      {...props}
    >
      {fkProps => {
        if (onUpdateValues)
          onUpdateValues(fkProps.values)

        return (
          <Grid container direction="column">

            <Grid container spacing={spacing}>
              {fields.map((f, index) => renderField(f, fkProps, index))}
            </Grid>

            {showLabelPolicy && (
              <LabelPolicy onChange={handleLabelPolicyChange} />
            )}

            {showReCAPTCHA && (
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_PUBLIC_KEY}
                onChange={handleReCAPTCHAChange}
                onExpired={handleReCAPTCHAExpiration}
                className={classes.btn}
                hl={'pt-BR'}
              />
            )}

            {renderErrorMsg()}
            {renderSuccessMsg()}

            <div style={showCloseBtn ? { marginTop: '15px' } : null}>
              <Grid container style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {
                  showCloseBtn ? (
                    <CleanButton className={classes.btn} onClick={onClose} style={{ marginRight: '15px' }}>
                      {t('default.cancel')}
                    </CleanButton>
                  ) : null
                }
                {
                  showBtn ? (
                    <PrimaryButton
                      className={classes.btn}
                      style={!showCloseBtn ? { width: '100%' } : null}
                      type='submit'
                      disabled={showReCAPTCHA ? (!successReCAPTCHA) : null}
                      {...btnProps}
                    >
                      {btnText}
                    </PrimaryButton>
                  ) : null
                }
              </Grid>
            </div>
          </Grid>
        )
      }
      }
    </Formik>
  )
}

LoginForm.propTyps = {
  fields: PropTypes.array.isRequired,
  btnText: PropTypes.string,
  btnProps: PropTypes.object,
  errorMsg: PropTypes.string,
  onSubmit: PropTypes.func,
  schema: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  showBtn: PropTypes.bool
}

LoginForm.defaultProps = {
  fields: [],
  btnText: '',
  btnProps: {},
  errorMsg: '',
  onSubmit: () => true,
  showBtn: true
}

const styles = theme => ({
  form: {
    display: 'flex',
    flex: 7,
  },
  field: {
    marginBottom: theme.spacing(1)
  },
  alert: {
    marginTop: theme.spacing(2)
  },
  btn: {
    marginTop: theme.spacing(2)
  },
})

export default withStyles(styles)(LoginForm)
